'use client';

import {
  getTypingTestRankingTestsTakenAction,
  getTypingTestRankingWpmAction,
  refetchTypingTestRankingsAction,
} from 'app/actions/typingTestRanking';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { LanguageResponse, TestResultSettingsRequest } from '@Root/src/api-generated';
import useLocalStorageStore from '@Root/src/store/useLocalStorageStore';
import useTypingStore from '@Root/src/store/useTypingStore';
import { TypingStats, UseTypingMode, Word } from '@Root/src/store/useTypingStore.types';

import LanguageModel from '@Api/models/LanguageModel';
import TypingTestResultModel, { TypingMode } from '@Api/models/TypingTestResultModel';
import TypingTestResultRankingTestsTakenListModel from '@Api/models/TypingTestResultRankingTestsTakenListModel';
import TypingTestResultRankingWpmModel from '@Api/models/TypingTestResultRankingWpmModel';
import { useCreateTypingTestResult } from '@Api/services/GameModeTypingTestService';

import Col from '@Components/atoms/Col';
import Row from '@Components/atoms/Row';
import { useGlobalContext } from '@Components/context/GlobalContext';
import { PropsWithClassName } from '@Components/helper';
import { unexpectedApiError } from '@Components/helper/error';
import { capitalizeFirstLetter, DEFAULT_LANGUAGE_ISO } from '@Components/helper/language';
import { getStaticLanguages } from '@Components/helper/languageJson';
import { errorToast } from '@Components/helper/toastHelper';
import { useTypingCheats } from '@Components/helper/useCheats';
import AdFreestarRow from '@Components/molecules/AdFreestarRow/AdFreestarRow';
import LanguageDropdownState from '@Components/molecules/LanguageDropdown/LanguageDropdownState';
import TypingTestHighscore from '@Components/organisms/TypingTestHighscore/TypingTestHighscore';
import { Routes } from '@Components/Routes';

import useRouter from '@Helpers/i18n/useRouter';
import { ShareMode } from '@Helpers/types/share';
import { removeWordWithChar } from '@Helpers/utils';

import { AdTypingTestWrapper, StyledModeSelectionState, StyledTypingBox } from './TypingTest.styles';

const TypingTestResult = dynamic(() => import('@Components/organisms/TypingTestResult/TypingTestResult'), {
  ssr: false,
});

interface Props extends PropsWithClassName {
  languageResponse: LanguageResponse;
  deviceType: string | undefined;
}

const TypingTest = (props: Props): React.ReactElement => {
  const { deviceType, languageResponse } = props;

  const languageModel = useMemo(() => LanguageModel.hydrateFromApi(languageResponse), [languageResponse]);

  const router = useRouter();

  const { loggedIn } = useGlobalContext();
  const languages = getStaticLanguages();
  const { wpm, durationInSeconds } = useTypingCheats();
  const minimumWpm = 1;
  const [typingTestResult, setTypingTestResult] = useState<TypingTestResultModel | undefined>(undefined);

  const { setTypingSettings, setDurationInSeconds, setText, makeWords, reset, resetStore } = useTypingStore(
    useShallow((state) => ({
      setTypingSettings: state.setTypingSettings,
      setDurationInSeconds: state.setDurationInSeconds,
      setText: state.setText,
      makeWords: state.makeWords,
      reset: state.reset,
      resetStore: state.resetStore,
    }))
  );
  const { typingBoxSettings } = useLocalStorageStore(
    useShallow((state) => ({
      typingBoxSettings: state.typingBoxSettings[UseTypingMode.TypingTest],
    }))
  );
  // we fetch settings.languageIso separately to avoid rerenders
  const typingMode = useTypingStore((state) => state.settings.typingMode);
  const languageIso = useTypingStore((state) => state.settings.languageIso);
  const isFinished = useTypingStore((state) => state.timing.isFinished);
  const text = useTypingStore((state) => state.settings.text);
  const shuffledText = useTypingStore((state) => state.settings.shuffledText);

  const resetTest = useCallback(() => {
    reset();
    setTypingTestResult(undefined);
  }, [reset]);

  // initial typing settings
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    let initialTypingMode = TypingMode.Normal;
    if (window.location.hash && window.location.hash.substring(1) === TypingMode.Advanced) {
      initialTypingMode = TypingMode.Advanced;
    }
    setTypingSettings({
      text: '', // words will be loaded in typingMode effect
      languageIso: languageModel.iso,
      languageLabel: capitalizeFirstLetter(languageModel.name),
      typingMode: initialTypingMode,
      shuffle: true,
      charactersLength: 2_000,
      durationInSeconds: durationInSeconds ?? 60,
      skipWords: true,
      mode: UseTypingMode.TypingTest,
    });
    makeWords();

    return () => {
      resetStore();
    };
  }, [durationInSeconds, languageModel.iso, languageModel.name, setTypingSettings, makeWords, resetStore]);

  // listen to cheat durationInSeconds and update settings
  useEffect(() => {
    if (durationInSeconds) setDurationInSeconds(durationInSeconds);
  }, [durationInSeconds, setDurationInSeconds]);

  useEffect(() => {
    if (languageIso === '' || languageIso === languageModel.iso) {
      return;
    }
    const languageName = languages.find((languageModel) => languageModel.iso === languageIso);

    if (!languageName) {
      router.push(`${Routes.TypingTest}${typingMode === TypingMode.Advanced ? `#${typingMode}` : ''}`);
      return;
    }

    router.push(
      `${Routes.TypingTest}/${languageName.name}${typingMode === TypingMode.Advanced ? `#${typingMode}` : ''}`
    );
  }, [languageIso, languageModel.iso, languages, router, typingMode]);

  useEffect((): void => {
    let words;
    if (typingMode === TypingMode.Advanced) {
      words = languageModel.top1000Words;
    } else {
      words = languageModel.top200Words;
    }
    if (!words) {
      return;
    }
    // remove unhandy chars for mobile keyboards
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      words = removeWordWithChar(words, '|', "'");
    }
    setText(words);
    resetTest();
  }, [typingMode, languageModel, deviceType, setText, resetTest]);

  const [rankingWpm, setRankingWpm] = useState<TypingTestResultRankingWpmModel[] | undefined>(undefined);
  const [rankingTestsTaken, setRankingTestsTaken] = useState<TypingTestResultRankingTestsTakenListModel | undefined>(
    undefined
  );
  const [isWpmLoading, setIsWpmLoading] = useState(true);
  const [isTestsTakenLoading, setIsTestsTakenLoading] = useState(true);

  useEffect(() => {
    if (!languageIso) {
      return;
    }

    let cancelled = false;
    setIsTestsTakenLoading(true);
    getTypingTestRankingTestsTakenAction()
      .then((json) => {
        if (cancelled) {
          return;
        }
        setRankingTestsTaken(TypingTestResultRankingTestsTakenListModel.hydrateFromApi(json));
      })
      .catch(unexpectedApiError)
      .finally(() => {
        if (!cancelled) {
          setIsTestsTakenLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [languageIso]);

  useEffect(() => {
    if (!languageIso) {
      return;
    }

    let cancelled = false;
    setIsWpmLoading(true);
    getTypingTestRankingWpmAction(languageIso, typingMode)
      .then((json) => {
        if (cancelled) {
          return;
        }
        setRankingWpm(json.data.map((item) => TypingTestResultRankingWpmModel.hydrateFromApi(item)));
      })
      .catch(unexpectedApiError)
      .finally(() => {
        if (!cancelled) {
          setIsWpmLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [languageIso, typingMode]);

  const { executeRequest: createResultExecuteRequest, result: createResult } = useCreateTypingTestResult(
    undefined,
    (error) => {
      errorToast(error);
    },
    (validationError) => {
      errorToast(validationError.message);
    }
  );

  const handleCreateTypingTestResult = async (stats: TypingStats, words: Word[]) => {
    await createResultExecuteRequest({
      body: {
        typingMode: typingMode,
        testResult: { ...stats, wpm: wpm ?? stats.wpm },
        words: words as unknown as { [key: string]: unknown },
        text: shuffledText ?? text,
        languageIso: languageIso || DEFAULT_LANGUAGE_ISO,
        settings: {
          fontFace: typingBoxSettings.fontFace,
          fontSize: typingBoxSettings.fontSize as unknown as TestResultSettingsRequest['fontSize'],
          rows: typingBoxSettings.rows,
          useHighContrast: typingBoxSettings.useHighContrast,
          useTimer: typingBoxSettings.useTimer,
          showStats: typingBoxSettings.showStats,
          showInvisibleChars: typingBoxSettings.showInvisibleChars,
        },
      },
    });
  };

  const refetchRankingsFromRoutes = useCallback(async () => {
    if (!languageIso) {
      return;
    }
    setIsWpmLoading(true);
    setIsTestsTakenLoading(true);
    try {
      const { wpm, testsTaken } = await refetchTypingTestRankingsAction(languageIso, typingMode);
      setRankingWpm(wpm.data.map((item) => TypingTestResultRankingWpmModel.hydrateFromApi(item)));
      setRankingTestsTaken(TypingTestResultRankingTestsTakenListModel.hydrateFromApi(testsTaken));
    } catch (e) {
      unexpectedApiError(e);
    } finally {
      setIsWpmLoading(false);
      setIsTestsTakenLoading(false);
    }
  }, [languageIso, typingMode]);

  useEffect(() => {
    if (createResult === null) {
      return;
    }

    setTypingTestResult(createResult);
    refetchRankingsFromRoutes().catch(unexpectedApiError);
  }, [createResult, refetchRankingsFromRoutes]);

  const languageDropdown = (
    <Row noGutters xsBottom>
      <LanguageDropdownState initialLanguageIso={languageModel.iso} />
    </Row>
  );

  const typingTestHighscore = useMemo(
    () => (
      <TypingTestHighscore
        isLoadingWpm={isWpmLoading}
        isLoadingTestsTaken={isTestsTakenLoading}
        rankingTestsTaken={rankingTestsTaken}
        rankingWpm={rankingWpm}
        typingMode={typingMode}
        language={languageModel}
      />
    ),
    [isTestsTakenLoading, isWpmLoading, rankingTestsTaken, rankingWpm, typingMode, languageModel]
  );

  const renderTypingTestResult = () => {
    if (!isFinished) {
      return;
    }

    return (
      <TypingTestResult
        result={typingTestResult}
        shareMode={ShareMode.TypingTest}
        minimumWpm={minimumWpm}
        resetTest={resetTest}
        fetchRanking={refetchRankingsFromRoutes}
      />
    );
  };

  return (
    <Row xsBottom dataTestId="TypingTest-root">
      <Col xs={12} sm={12} md={12} lg={10} lgOffset={1}>
        <AdTypingTestWrapper>
          {!isFinished && (
            <StyledTypingBox
              headerLeft={languageDropdown}
              headerRight={<StyledModeSelectionState />}
              typingCallbacks={{
                onFinish: async (stats, words) => {
                  if (loggedIn && stats.wpm >= minimumWpm) {
                    // todo we might make this request in the store as well and dont use callbacks here
                    await handleCreateTypingTestResult(stats, words);
                  }
                },
              }}
            />
          )}
          {renderTypingTestResult()}
          <AdFreestarRow />
        </AdTypingTestWrapper>
        {typingTestHighscore}
      </Col>
    </Row>
  );
};

export default TypingTest;
